import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

// Centrale JOH Admin → BEDRIJVEN: dashboard met statistieken, zoeken en bedrijfskaarten.
export default function AdminCompanies() {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", official_website: "", contact_name: "", contact_email: "", contact_phone: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/companies").then(({ data }) => setData(data)).catch((err) => {
      const s = err?.response?.status;
      if (s === 401) {
        localStorage.removeItem("joh_admin_token");
        nav(`/admin/login?next=${encodeURIComponent(pathname)}`, { replace: true });
      } else if (s === 403) nav("/admin", { replace: true });
      else toast.error("Bedrijven laden mislukt");
    });
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    if (!form.name.trim()) { toast.error("Bedrijfsnaam is verplicht"); return; }
    setBusy(true);
    try {
      const { data: c } = await api.post("/companies", form);
      toast.success("Bedrijf aangemaakt");
      nav(`/admin/bedrijven/${c.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aanmaken mislukt");
      setBusy(false);
    }
  };

  if (!data) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><span className="chip text-[#4A4A4A]" data-testid="companies-loading">BEDRIJVEN …</span></div>;

  const stats = [
    ["Bedrijven", data.stats.companies_total], ["Actief", data.stats.companies_active],
    ["Projecten", data.stats.projects_total], ["Leden", data.stats.members_total],
    ["Komende sessies", data.stats.upcoming_sessions], ["Open facturen", data.stats.invoices_open],
  ];
  const statusColor = { active: "text-[#7BAF7B]", inactive: "text-[#D4A93A]", archived: "text-[#4A4A4A]" };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F3F0] px-5 md:px-10 py-8" data-testid="admin-companies-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <Link to="/admin" className="chip text-[#A3A3A3] hover:text-[#F5F3F0] inline-flex items-center gap-2 transition-colors" data-testid="companies-back"><ArrowLeft size={12} /> ADMIN</Link>
          <h1 className="font-display text-3xl md:text-4xl uppercase mt-3" data-testid="companies-title">Bedrijven</h1>
        </div>
        <button onClick={() => setAdding(true)} className="btn-light inline-flex items-center gap-2" data-testid="company-add-open"><Plus size={14} /> BEDRIJF TOEVOEGEN</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[#F5F3F0]/10 mt-10 border border-[#F5F3F0]/10" data-testid="companies-stats">
        {stats.map(([k, v]) => (
          <div key={k} className="bg-[#0A0A0A] px-4 py-5" data-testid={`companies-stat-${k.toLowerCase().replace(/ /g, "-")}`}>
            <div className="chip text-[#4A4A4A]">{k}</div>
            <div className="font-display text-3xl mt-2">{v}</div>
          </div>
        ))}
      </div>

      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ZOEK BEDRIJF…"
        className="editorial-input dark w-full md:w-96 mt-8" data-testid="companies-search" />

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-6" data-testid="companies-list">
        {data.items.filter((c) => c.name.toLowerCase().includes(q.toLowerCase())).map((c) => (
          <Link key={c.id} to={`/admin/bedrijven/${c.id}`}
            className="border border-[#F5F3F0]/15 p-5 hover:border-[#D4A93A]/60 transition-colors group" data-testid={`company-card-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {c.logo_url
                  ? <img src={c.logo_url} alt="" className="w-10 h-10 object-contain shrink-0" />
                  : <div className="w-10 h-10 border border-[#F5F3F0]/20 flex items-center justify-center chip text-[#4A4A4A] shrink-0">{c.name.slice(0, 2)}</div>}
                <div className="min-w-0">
                  <div className="font-display text-lg uppercase truncate group-hover:text-[#D4A93A] transition-colors">{c.name}</div>
                  <div className={`chip mt-1 ${statusColor[c.status] || "text-[#A3A3A3]"}`} data-testid={`company-status-${c.id}`}>{c.status === "active" ? "ACTIEF" : c.status === "inactive" ? "INACTIEF" : "GEARCHIVEERD"}</div>
                </div>
              </div>
            </div>
            <div className="mt-4 space-y-1.5">
              {c.projects.map((p) => (
                <div key={p.id} className="flex items-center justify-between text-sm">
                  <span className="text-[#A3A3A3] truncate">{p.name}</span>
                  <span className="chip text-[#4A4A4A]">/{p.slug}</span>
                </div>
              ))}
              {c.projects.length === 0 && <div className="text-sm text-[#4A4A4A]">Nog geen projecten</div>}
            </div>
            <div className="mt-4 pt-4 border-t border-[#F5F3F0]/10 flex items-center justify-between">
              <span className="chip text-[#4A4A4A]">{c.member_count} TEAM</span>
              {c.official_website && (
                <span className="chip text-[#4A4A4A] inline-flex items-center gap-1 truncate max-w-[55%]">
                    <ExternalLink size={10} /> {c.official_website.replace(/^https?:\/\//, "")}
                </span>
              )}
            </div>
          </Link>
        ))}
        {data.items.length === 0 && <p className="text-[#4A4A4A] text-sm" data-testid="companies-empty">Nog geen bedrijven.</p>}
      </div>

      {adding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="company-add-modal">
          <div className="absolute inset-0 bg-black/80" onClick={() => setAdding(false)} />
          <div className="relative bg-[#0A0A0A] border border-[#F5F3F0]/15 p-6 md:p-8 w-full max-w-lg">
            <h2 className="font-display text-xl uppercase" data-testid="company-add-title">Bedrijf toevoegen</h2>
            <div className="space-y-4 mt-6">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="BEDRIJFSNAAM *" className="editorial-input dark w-full" data-testid="company-add-name" />
              <input value={form.official_website} onChange={(e) => setForm({ ...form, official_website: e.target.value })} placeholder="OFFICIËLE WEBSITE (https://…)" className="editorial-input dark w-full" data-testid="company-add-website" />
              <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="CONTACTPERSOON" className="editorial-input dark w-full" data-testid="company-add-contact" />
              <input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="E-MAIL CONTACT" className="editorial-input dark w-full" data-testid="company-add-email" />
              <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="TELEFOON" className="editorial-input dark w-full" data-testid="company-add-phone" />
            </div>
            <div className="flex gap-3 mt-8">
              <button onClick={create} disabled={busy} className="btn-light flex-1 justify-center disabled:opacity-40" data-testid="company-add-submit">{busy ? "…" : "AANMAKEN"}</button>
              <button onClick={() => setAdding(false)} className="btn-ghost-dark text-[#A3A3A3] border-[#F5F3F0]/25" data-testid="company-add-cancel">ANNULEREN</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
